/**
 * Регистрация push-токена устройства (Android RuStore / iOS APNs) на сервере.
 *
 * Токен кладёт локальный бандл (mobile/client-app, Capacitor Preferences,
 * ключ `domcrm_push_token`). Здесь читаем его через нативный мост и вызываем
 * POST /auth/push-tokens с access-токеном сессии.
 *
 * Также слушаем событие `registration` (токен может прийти ПОСЛЕ редиректа
 * на remote embed) и событие тапа по уведомлению (deep_link).
 */

import { postRegisterPushToken, resolveIosPushAppId, resolvePushAppId } from '../widget-api/push-token';
import { getGlobalEmbedSession } from './embed-session-store';

const PUSH_TOKEN_KEY = 'domcrm_push_token';
const PUSH_DEEPLINK_KEY = 'domcrm_push_deeplink';

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: {
    Preferences?: {
      get?: (opts: { key: string }) => Promise<{ value?: string | null }>;
      set?: (opts: { key: string; value: string }) => Promise<unknown>;
    };
    PushNotifications?: {
      addListener?: (
        eventName: string,
        listener: (data: { value?: string; data?: Record<string, unknown>; deep_link?: string }) => void,
      ) => Promise<unknown>;
    };
    RuStorePush?: {
      getToken?: () => Promise<{ token?: string }>;
    };
  };
};

function capacitor(): CapacitorGlobal | null {
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap?.isNativePlatform?.() ? cap : null;
}

async function readNativePushToken(): Promise<string | null> {
  const cap = capacitor();
  if (cap) {
    try {
      const res = await cap.Plugins?.Preferences?.get?.({ key: PUSH_TOKEN_KEY });
      const token = res?.value;
      if (token && token.trim()) return token.trim();
    } catch {
      /* переходим к postMessage-мосту */
    }
  }
  // Локальный бандл (нативный Capacitor) недоступен из remote-iframe —
  // запрашиваем токен у родителя (App.tsx отдаёт его из нативных Preferences).
  return requestTokenFromNative();
}

/** Запрос push-токена у локального бандла через postMessage (мост native ↔ embed). */
function requestTokenFromNative(): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; payload?: { token?: string } } | undefined;
      if (data?.type !== 'domcrm:push-token') return;
      settled = true;
      window.removeEventListener('message', onMessage);
      const token = data.payload?.token;
      resolve(token && token.trim() ? token.trim() : null);
    };
    window.addEventListener('message', onMessage);
    try {
      window.parent?.postMessage({ type: 'domcrm:get-push-token' }, '*');
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, 2500);
  });
}

async function saveNativePushToken(token: string): Promise<void> {
  const cap = capacitor();
  if (!cap) return;
  try {
    await cap.Plugins?.Preferences?.set?.({ key: PUSH_TOKEN_KEY, value: token });
  } catch {
    /* не критично */
  }
}

/** Регистрирует сохранённый push-токен (best-effort, после логина клиента).
 *  iOS (нативный WebView без RuStore-плагина) уходит как provider=apns/platform=ios
 *  с iOS Bundle ID (com.domcrm.clientapp.<org_slug> / global / trainer),
 *  иначе сервер запишет токен как RuStore (Android-ключ com.domcrm.client) и Apple-пуши работать не будут.
 */
export async function registerPushTokenIfPresent(): Promise<void> {
  const token = await readNativePushToken();
  if (!token) return;
  try {
    const session = getGlobalEmbedSession();
    const cap = capacitor();
    const isIOS = cap?.getPlatform?.() === 'ios' && !cap?.Plugins?.RuStorePush?.getToken;
    await postRegisterPushToken(
      isIOS
        ? { device_token: token, app_id: resolveIosPushAppId(), provider: 'apns', platform: 'ios' }
        : { device_token: token, app_id: resolvePushAppId() },
      { accessToken: session?.accessToken ?? undefined },
    );
  } catch {
    /* не критично: повторится при следующем входе */
  }
}

/**
 * Слушатель регистрации APNs-токена: токен может прийти после редиректа на
 * remote embed — ловим событие `registration` и сохраняем в Preferences.
 */
export async function initPushRegistrationListener(): Promise<void> {
  const cap = capacitor();
  if (!cap?.Plugins?.PushNotifications?.addListener) return;
  try {
    await cap.Plugins.PushNotifications.addListener('registration', async (data) => {
      const token = data?.value;
      if (token) {
        await saveNativePushToken(token);
        void registerPushTokenIfPresent();
      }
    });
  } catch {
    /* плагин может быть недоступен (не нативная сборка) */
  }
}

/**
 * Тап по push-уведомлению → сохранение deep_link и переход в embed.
 */
export async function initPushTapHandling(): Promise<void> {
  const cap = capacitor();
  if (!cap?.Plugins?.PushNotifications?.addListener) return;
  try {
    await cap.Plugins.PushNotifications.addListener('pushNotificationActionPerformed', async (data) => {
      const deepLink = typeof data?.deep_link === 'string' ? data.deep_link : String(data?.data?.deep_link ?? '');
      if (!deepLink) return;
      try {
        await cap.Plugins?.Preferences?.set?.({ key: PUSH_DEEPLINK_KEY, value: deepLink });
      } catch {
        /* ignore */
      }
      // Грубый переход: embed — SPA, путь вида /my-bookings открываем на текущем хосте.
      window.location.href = `/embed${deepLink}`;
    });
  } catch {
    /* ignore */
  }
}

/** Инициализация всего (listener + tap) и регистрация при наличии токена. */
export async function initPushRegistration(): Promise<void> {
  await Promise.all([initPushRegistrationListener(), initPushTapHandling()]);
  await registerPushTokenIfPresent();
}
